import React, { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Button,
  Card,
  Divider,
  Grid,
  GridContainer,
  Header,
  Icon,
  Input,
  Row,
  Text,
} from '@plurall/elo-beta'
import LoadingContainer from 'App/components/LoadingContainer/LoadingContainer'
import MainMenu from 'App/components/MainMenu/MainMenu'
import { USE_ACCESS_CODE } from 'graphql/mutations'
import { PERSON_USED_ACCESS_CODES } from 'graphql/queries'
import { useForm } from 'hooks'
import ErrorContext from 'stores/ErrorStore'
import UserContext from 'stores/UserStore'

import {
  apiRedirectUrl,
  graphqlClient,
  handleGraphQLErrors,
  handleGraphQLErrorsWithToast,
  isManageable,
  redirectToHome,
} from 'utils'
import { ROUTES } from 'utils/constants'

import styles from './AccessCode.module.css'
import AccessCodeDialog from './AccessCodeDialog'
import { accessCodeValidate } from './validations'

const ERROR_MESSAGE =
  ' Verifique se o código foi digitado corretamente ou insira um novo código. Se o problema persistir, entre em contato com nosso suporte'

const INCOMPATIBLES_ERROR_CODE = ['Code: 003', 'Code: 009', 'Code: 013']

const AccessCode = () => {
  const userStore = useContext(UserContext)
  const errorStore = useContext(ErrorContext)
  const [firstAccess, setFirstAccess] = useState(true)
  const [code, setCode] = useState(null)
  const [linkToRedirect, setLinkToRedirect] = useState(null)
  const [codeInfo, setCodeInfo] = useState(null)
  const [dialog, setDialog] = useState(null)
  const [dialogInfo, setDialogInfo] = useState(null)
  const [usedAccessCodes, setUsedAccessCodes] = useState(null)
  const [loadingUsedCodes, setLoadingUsedCodes] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    getUsedAccessCodes()
  }, [])

  const burnAccessCode = async accessCode => {
    if (!accessCode) {
      return null
    }

    const { data } = await graphqlClient().mutate({
      mutation: USE_ACCESS_CODE,
      variables: {
        accessCode,
      },
    })

    return data
  }

  const getUsedAccessCodes = async () => {
    try {
      const { data } = await graphqlClient().query({
        query: PERSON_USED_ACCESS_CODES,
      })

      setUsedAccessCodes(data)
    } catch (e) {
      handleGraphQLErrorsWithToast(
        e,
        'Não foi possível carregar os códigos de acesso. Tente novamente mais tarde.',
      )
    } finally {
      setLoadingUsedCodes(false)
    }
  }

  const renderActiveButton = code => (
    <div className={styles.buttons}>
      <Button
        type='submit'
        dataTestId='btn-submit'
        className={styles.button}
        onClick={() =>
          onUseAccessCode(
            code,
            burnAccessCode,
            values,
            userStore,
            errorStore,
            handleChange,
            useStates,
          )
        }
      >
        Carregar código
      </Button>
    </div>
  )

  const renderInput = () => {
    return (
      <div>
        <Input
          dataTestId='input-submit'
          type='text'
          placeholder='Ex.: ACPBA7NVC'
          onChange={value => setCode(value)}
        />
      </div>
    )
  }

  const setCodeInfoAccess = (data, useStates) => {
    if (data.useAccessCode?.products?.length > 0) {
      useStates.setCodeInfo(data.useAccessCode.products[0])
      useStates.userStore.setNeedSelectProfile(true)
      return useStates.setDialog(true)
    }

    if (data.useAccessCode?.school) {
      useStates.setCodeInfo(data.useAccessCode.school)
      useStates.userStore.setSchoolAccessCode(data.useAccessCode?.accessCode)
      useStates.userStore.setCurrentSchool(data.useAccessCode.school)
      useStates.userStore.setNeedSelectProfile(true)
      return useStates.setDialog(true)
    }

    if (data.useAccessCode?.class?.school) {
      useStates.setCodeInfo(data.useAccessCode.class.school)
      useStates.userStore.setNeedSelectProfile(true)
      return useStates.setDialog(true)
    }
  }

  const renderAddCodeForm = (code, onSubmitForm) => (
    <form onSubmit={onSubmitForm} className={styles.content}>
      <Card
        title='Insira seu novo código de acesso:'
        renderHeaderActions={() => {}}
        className={styles.accessCode}
      >
        <div className={styles.cardContent}>
          <div className={styles.row}>
            <Grid desktop={4}>{renderInput()}</Grid>
            <Grid desktop={4}>{renderActiveButton(code)}</Grid>
          </div>
        </div>
      </Card>
    </form>
  )

  const formatDate = value => new Date(value).toLocaleDateString('pt-BR')

  const getSchoolName = code => {
    if (code.school) {
      return code.school.tradeName
    }
    if (code.class && code.class.school) {
      return code.class.school.tradeName
    }
    return ''
  }

  const renderItemImage = code => (
    <Icon shy size='large' type={code?.products[0] ? 'Book' : 'Store'} />
  )

  const renderItemInfo = (code, index) => {
    const schoolName = getSchoolName(code)

    return (
      <>
        <div className={styles.usedOn}>
          <Text dataTestId={`date-used-${index}`} size='t4'>
            Utilizado em {formatDate(code.createdAt)}
          </Text>
        </div>
        <div>
          <Text size='t3' semibold>
            Código:&nbsp;
          </Text>
          <Text dataTestId={`used-code-${index}`} size='t3'>
            {code?.accessCode}
          </Text>
        </div>
        <div>
          {code?.products && code?.products[0] && code?.products[0]?.title && (
            <>
              <Text size='t3' semibold>
                Material: &nbsp;
              </Text>
              <Text dataTestId={`material-title-${index}`}> {code?.products[0]?.title} </Text>
            </>
          )}
          {schoolName && (
            <>
              <Text size='t3' semibold>
                Escola: &nbsp;
              </Text>
              <Text dataTestId={`school-title-${index}`}>{schoolName}</Text>
            </>
          )}
        </div>
      </>
    )
  }

  const renderAllCodes = usedAccessCodes => {
    return usedAccessCodes.map((code, index) => (
      <>
        <Divider className={styles.divider} light />
        <div className={styles.codeContainer}>
          <div className={styles.codeImage}>{renderItemImage(code)}</div>
          <div className={styles.codeInfo}>{renderItemInfo(code, index)}</div>
        </div>
      </>
    ))
  }

  const renderUsedCodesForm = person => (
    <Card
      title='Histórico'
      description='Visualize todos os códigos que já foram carregados em sua conta.'
      renderHeaderActions={() => {}}
      className={styles.cardAccessCode}
    >
      <div className={styles.cardContent}>{renderAllCodes(person.usedAccessCodes)}</div>
    </Card>
  )

  const renderErrorCodeMessage = error => {
    if (INCOMPATIBLES_ERROR_CODE.find(err => err.includes(error[1]))) {
      return {
        title: 'Código incompatível',
        message: `${error[0]} ${ERROR_MESSAGE}`,
      }
    }
    return {
      title: 'Código inválido',
      message: `${error[0]} ${ERROR_MESSAGE}`,
    }
  }

  const onUseAccessCode = async (
    code,
    burnAccessCode,
    values,
    userStore,
    errorStore,
    handleChange,
    useStates,
  ) => {
    useStates.setDialog(null)

    try {
      const data = await burnAccessCode(code)

      userStore.setUsedAccessCode(data.useAccessCode)
      useStates.setDialogInfo({ type: 'positive', title: 'Código validado', action: true })
      setCodeInfoAccess(data, useStates)

      return true
    } catch (error) {
      if (error.graphQLErrors) {
        const graphQLErrors = handleGraphQLErrors(error.graphQLErrors)
        await errorStore.setError(values.accessCode, graphQLErrors[0], graphQLErrors[1])
        handleChange({ name: 'accessCode', value: '' })
        useStates.setDialogInfo({ type: 'negative', ...renderErrorCodeMessage(graphQLErrors) })
        useStates.setDialog(true)
        return true
      }
      return false
    }
  }

  const apiRedirect = apiRedirectUrl()

  useLayoutEffect(() => {
    if (usedAccessCodes && firstAccess) {
      const { person } = usedAccessCodes
      if (person.schools.length > 0 && !isManageable(person.schools)) {
        redirectToHome()
        return
      }
      if (!userStore.person.usedAccessCodes) {
        userStore.setUsedAccessCodes(person.usedAccessCodes)
      }
      setFirstAccess(false)
    }
  }, [usedAccessCodes, firstAccess, userStore])

  const useStates = {
    linkToRedirect,
    setLinkToRedirect,
    setCodeInfo,
    codeInfo,
    userStore,
    dialog,
    setDialog,
    dialogInfo,
    setDialogInfo,
  }

  const onValidForm = async values =>
    onUseAccessCode(burnAccessCode, values, userStore, errorStore, handleChange, useStates)

  const fields = { accessCode: '' }
  const { values, handleChange, onSubmitForm } = useForm(fields, onValidForm, accessCodeValidate)

  if (loadingUsedCodes || (usedAccessCodes && firstAccess)) {
    return <LoadingContainer text='Carregando códigos de acesso...' fullscreen />
  }

  if (linkToRedirect && linkToRedirect === apiRedirect) {
    return window.location.assign(linkToRedirect)
  }

  if (linkToRedirect) {
    navigate(linkToRedirect)
  }

  return (
    <>
      <Header description='Gerencie seus códigos de acesso relacionados a materiais e escolas.'>
        Código de acesso
      </Header>

      <GridContainer>
        <Row>
          <MainMenu />
          <Grid desktop={9}>
            {renderAddCodeForm(code, onSubmitForm)}

            {renderUsedCodesForm(userStore.person)}
          </Grid>
        </Row>
      </GridContainer>

      {dialog && dialogInfo && (
        <AccessCodeDialog
          dialog={dialogInfo}
          codeInfo={codeInfo}
          errorStore={errorStore}
          navigate={() => useStates.setLinkToRedirect(ROUTES.PERFIL_DADOS_ESCOLARES_FUNCOES)}
          dismiss={() => useStates.setDialog(null)}
        />
      )}
    </>
  )
}
export default AccessCode
